import React, { useState, useCallback } from "react";
import Select from "react-select";
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import {
  getCroppedImg,
  UpdateTheme,
  DeleteImage
} from "./Requests";
import "./Theme.css";
import Cropper from "react-easy-crop";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { notifyError, notifySuccess } from "../../Response/ResposeHandler";
import { useTranslation } from "react-i18next";

const Theme = (props) => {
  const { t } = useTranslation("miniSiteTheme");

  const themes = [
    { value: 1, label: t("themes.beigeTheme") },
    { value: 2, label: t("themes.whiteGreyTheme") }
  ];

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppingFor, setCroppingFor] = useState(null);
  const [logo, setLogo] = useState("data:image/jpeg;base64," + props.site.logoImage);
  const [header, setHeader] = useState("data:image/jpeg;base64," + props.site.headerImage);
  const [theme, setTheme] = useState(
    themes.find((x) => x.value === props.site.themeId) || themes[0]
  );
  const [saveThemeButtonLoading, setSaveThemeButtonLoading] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleImageChange = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImageSrc(imageUrl);
      setCroppingFor(type); // 'logo' | 'header'
    }
  };

  const handleCropDone = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      setImageSrc(null);
      setCroppingFor(null);
      return;
    }

    try {
      const isLogo = croppingFor === "logo";

      // Output sizes/quality
      const HEADER_WIDTH = 2400;
      const HEADER_HEIGHT = Math.round(HEADER_WIDTH * (180 / 370));
      const HEADER_QUALITY = 0.95;
      const LOGO_SIZE = 1024;

      const result = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        isLogo
          ? {
              targetWidth: LOGO_SIZE,
              targetHeight: LOGO_SIZE,
              mime: "image/png",
              quality: 1,
              returnPreviewDataURL: true
            }
          : {
              targetWidth: HEADER_WIDTH,
              targetHeight: HEADER_HEIGHT,
              mime: "image/jpeg",
              quality: HEADER_QUALITY,
              returnPreviewDataURL: true
            }
      );

      const preview = typeof result === "string" ? result : result?.dataURL;

      if (isLogo) setLogo(preview);
      else setHeader(preview);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Crop failed:", err);
    } finally {
      setImageSrc(null);
      setCroppingFor(null);
    }
  };

  const handleCropCancel = () => {
    setImageSrc(null);
    setCroppingFor(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  function base64ToFile(base64Data, filename) {
    const arr = base64Data.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1] || "");
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  function onSubmit() {
    setSaveThemeButtonLoading(true);
    const headerFile = base64ToFile(header, "header.jpg");
    const logoFile = base64ToFile(logo, "logo.jpg");

    UpdateTheme(
      theme?.value ?? null,
      logo === "data:image/jpeg;base64,null" ? null : logoFile,
      header === "data:image/jpeg;base64,null" ? null : headerFile
    )
      .then(() => {
        notifySuccess(t("toasts.themeSaved"));
      })
      .catch(() => {
        notifyError(t("toasts.genericError"));
      })
      .finally(() => {
        setSaveThemeButtonLoading(false);
        props.getSite?.();
      });
  }

  function handleDeleteImage(imageType) {
    DeleteImage(imageType)
      .then(() => {
        notifySuccess(
          t("toasts.imageDeleted", {
            type:
              imageType === "logo"
                ? t("labels.logo")
                : imageType === "header"
                ? t("labels.header")
                : imageType
          })
        );
        if (imageType === "logo") setLogo("data:image/jpeg;base64,null");
        if (imageType === "header") setHeader("data:image/jpeg;base64,null");
      })
      .catch(() => {
        notifyError(t("toasts.genericError"));
      })
      .finally(() => {
        props.getSite?.();
      });
  }

  return (
    <div className="up-wrap">
      <ToastContainer />

      {/* Theme chooser */}
      <section className="up-card">
        <div className="gen-cardHead">
          <h3 className="gen-h3">{t("titles.chooseTheme")}</h3>
          <p className="gen-muted">{t("descriptions.chooseThemeHint")}</p>
        </div>

        <div className="up-field">
          <Select
            isSearchable={false}
            placeholder={t("placeholders.selectTheme")}
            closeMenuOnSelect
            options={themes}
            onChange={(e) => setTheme({ value: e.value, label: e.label })}
            value={theme}
            className="sm-select"
            classNamePrefix="rs"
          />
        </div>
      </section>

      {/* Logo & Header */}
      <div className="up-grid">
        {/* Logo */}
        <section className="up-card">
          <div className="gen-cardHead">
            <h3 className="gen-h3">{t("titles.logo")}</h3>
            {logo === "data:image/jpeg;base64,null" && (
              <p className="up-sub">{t("descriptions.logoHint")}</p>
            )}
          </div>

          <div className="up-preview up-preview--logo">
            {logo !== "data:image/jpeg;base64,null" ? (
              <img src={logo} alt={t("labels.logoPreviewAlt")} />
            ) : (
              <div className="up-empty">{t("empty.noLogo")}</div>
            )}
          </div>

          <div className="up-actions">
            <label className="up-btn">
              {t("actions.uploadLogo")}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  handleImageChange(e, "logo");
                  e.target.value = null;
                }}
              />
            </label>

            <button
              type="button"
              className="up-btn up-btn--ghost"
              onClick={() => handleDeleteImage("logo")}
            >
              {t("actions.delete")}
            </button>
          </div>
        </section>

        {/* Header */}
        <section className="up-card">
          <div className="gen-cardHead">
            <h3 className="gen-h3">{t("titles.header")}</h3>
            {header === "data:image/jpeg;base64,null" && (
              <p className="up-sub">{t("descriptions.headerHint")}</p>
            )}
          </div>

          <div className="up-preview up-preview--header">
            {header !== "data:image/jpeg;base64,null" ? (
              <img src={header} alt={t("labels.headerPreviewAlt")} />
            ) : (
              <div className="up-empty">{t("empty.noHeader")}</div>
            )}
          </div>

          <div className="up-actions">
            <label className="up-btn">
              {t("actions.uploadHeader")}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  handleImageChange(e, "header");
                  e.target.value = null;
                }}
              />
            </label>

            <button
              type="button"
              className="up-btn up-btn--ghost"
              onClick={() => handleDeleteImage("header")}
            >
              {t("actions.delete")}
            </button>
          </div>
        </section>
      </div>

      {/* Save */}
      <div className="infoActions">
        <CustomButton
          disabled={saveThemeButtonLoading}
          className="app-btn--sm"
          onClick={onSubmit}
          label={t("actions.saveTheme")}
          loading={saveThemeButtonLoading}
        />
      </div>

      {/* Cropper modal */}
      {imageSrc && (
        <div className="up-modal" role="dialog" aria-modal="true">
          <div className="up-modal__panel">
            <header className="up-modal__head">
              <h4 className="up-title">
                {t("titles.crop", {
                  type:
                    croppingFor === "logo" ? t("labels.logo") : t("labels.header")
                })}
              </h4>
              <button
                className="up-close"
                onClick={handleCropCancel}
                aria-label={t("actions.close")}
                title={t("actions.close")}
              >
                ×
              </button>
            </header>

            <div className="up-cropArea">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={croppingFor === "logo" ? 1 : 370 / 180}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <footer className="up-modal__actions">
              <button className="up-btn up-btn--ghost" onClick={handleCropCancel}>
                {t("actions.cancel")}
              </button>
              <button className="up-btn up-btn--primary" onClick={handleCropDone}>
                {t("actions.cropAndSave")}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Theme;
